import { getAlterForGeburtstag, getFormattedDateString } from '@mng/mng-ts-helpers';
import { produce } from 'immer';
import { GvAngebotData, GvPerson, GvPersonBeziehungen } from '../../gvangebot.model.types';
import { Person } from '../../antrag.model.types';
import { sozVerAnstaltenDeckungsPflicht } from '../gvperson.validation';

export class SozialversicherungKeys {
  static GKK = '10'; // Gebietskrankenkasse
  static LKUF = '83'; // Lehrer- Kranken- und Unfallfürsorge für Oberösterreich
  static KFG = '91'; // Krankenfürsorge für Gemeindebedienstete
  static KFL = '92'; // Krankenfürsorge für Landesbedienstete
  static AUSLAND = '7'; // Sozialversicherung Ausland
}

export const BMI_MAX = 40;
export const VN_BEZ_OPTION_VALUE_EHEGATTE_LEBENSGEFAEHRTE = '2';
export const VN_BEZ_OPTION_VALUE_KIND = '3';
export const VN_BEZ_OPTION_VALUE_SONSTIGER = '4';
export const VN_BEZ_OPTION_VALUE_KEIN_ANGEHOERIGER = '5';

export function calcBmi(groesse: number, gewicht: number): number {
  if (!groesse || !gewicht) {
    return 0;
  }
  // BMI auf 2 Kommastellen gerundet
  const bmi: number = Math.round((gewicht / Math.pow(groesse / 100, 2)) * 100) / 100;
  return bmi;
}

export const RIKO_ABLEHNUNG = 99999;

export function bmiImUngueltigenBereich(bmi: number): boolean {
  return bmi > BMI_MAX;
}

export function isElternteilWaehlbar(gvPerson: GvPerson, gvAngebotData: GvAngebotData): boolean {
  if (
    gvPerson.stammdaten.juristischePerson ||
    gvAngebotData.gvPersonenData.personen.filter((person) => person.personRollen.vp).length <= 1
  ) {
    return false;
  } else {
    const alter = getAlterForGeburtstag(gvPerson.stammdaten.gebdatum);
    const altersGrenze = getAltersGrenzeFuerElternteilFrage(gvPerson.stammdaten.sozversanstalt);

    const isKind: boolean = alter < altersGrenze;
    if (!isKind) {
      return false;
    }
    return gvPerson.personRollen.vp;
  }
}

export function getAltersGrenzeFuerElternteilFrage(sozversanstalt: number): number {
  if (+SozialversicherungKeys.LKUF === sozversanstalt) {
    return 29;
  } else if (+SozialversicherungKeys.KFL === sozversanstalt || +SozialversicherungKeys.KFG === sozversanstalt) {
    return 27;
  }
  return 25;
}

export function getAltersGrenzeFuerAusbildungsFrage(
  sozversanstalt: number,
  fzKontoNummer: string,
  vertragsAenderung: boolean
): number {
  const SPEZIAL_FZ_KONTEN = ['3963', '9070'];
  if (+SozialversicherungKeys.LKUF === sozversanstalt) {
    return 29;
  } else if (+SozialversicherungKeys.KFL === sozversanstalt || +SozialversicherungKeys.KFG === sozversanstalt) {
    return 27;
  } else if (SPEZIAL_FZ_KONTEN.includes(fzKontoNummer)) {
    return 25;
  }

  if (vertragsAenderung) {
    if (SPEZIAL_FZ_KONTEN.includes(fzKontoNummer)) {
      return 25;
    }
  }
  return 17; // Frage wird im NEUGESCHÄFT nicht gestellt da Mindestalter für Frage is 18
}

export function getAllNichtWaehlbareElternteile(gvAngebotData: GvAngebotData, gvPerson: GvPerson): GvPerson[] {
  return gvAngebotData.gvPersonenData.personen.filter(
    (person) =>
      getAlterForGeburtstag(person.stammdaten.gebdatum) - getAlterForGeburtstag(gvPerson.stammdaten.gebdatum) < 13 ||
      (!person.personRollen.vn && !person.personRollen.vp)
  );
}

export function findGvPerson(gvAngebotData: GvAngebotData, id: string): GvPerson {
  return gvAngebotData.gvPersonenData.personen.find((person) => person.id === id);
}

export function isElternteilVnForPersonen(personen: GvPerson[], personBeziehungen: GvPersonBeziehungen): boolean {
  const vnElternteil: GvPerson = personen.find(
    (person) =>
      personBeziehungen &&
      ((personBeziehungen.elternteilPersonId &&
        +person.id === personBeziehungen.elternteilPersonId &&
        person.personRollen &&
        person.personRollen.vn) ||
        (personBeziehungen.elternteilPersonId2 &&
          +person.id === personBeziehungen.elternteilPersonId2 &&
          person.personRollen &&
          person.personRollen.vn))
  );
  return !!vnElternteil;
}

export function isElternteilVn(gvAngebotData: GvAngebotData, personBeziehungen: GvPersonBeziehungen): boolean {
  return isElternteilVnForPersonen(gvAngebotData.gvPersonenData.personen, personBeziehungen);
}

export function isElternteil(gvAngebotData: GvAngebotData, person: GvPerson): boolean {
  const result = !!gvAngebotData.gvPersonenData.personen.find(
    (currentPerson) =>
      currentPerson.personBeziehungen &&
      (+person.id === currentPerson.personBeziehungen.elternteilPersonId ||
        +person.id === currentPerson.personBeziehungen.elternteilPersonId2)
  );
  return result;
}

export function getAllPersonenExcept(gvAngebotData: GvAngebotData, exceptPersonen: GvPerson[]): GvPerson[] {
  const findPerson = (person: GvPerson) =>
    exceptPersonen.find((currentPerson: Person) => currentPerson.id === person.id);
  const result: GvPerson[] = gvAngebotData.gvPersonenData.personen.filter((person: GvPerson) => !findPerson(person));
  return result;
}

export function isVnBeziehungEhegatteMoeglich(gvAngebotData: GvAngebotData, person: GvPerson) {
  let result;
  if (VN_BEZ_OPTION_VALUE_EHEGATTE_LEBENSGEFAEHRTE === person.personBeziehungen?.vnBeziehung) {
    result = true;
  } else {
    result = !gvAngebotData.gvPersonenData.personen.find(
      (currentPerson) => VN_BEZ_OPTION_VALUE_EHEGATTE_LEBENSGEFAEHRTE === currentPerson.personBeziehungen?.vnBeziehung
    );
  }
  return result;
}

function setzeVnAlsElternteilWennNotwendig(
  gvAngebotData: GvAngebotData,
  personBeziehungen: GvPersonBeziehungen,
  personBeziehungenUpdate: GvPersonBeziehungen
): GvPersonBeziehungen | undefined {
  const vn: GvPerson = findVn(gvAngebotData);
  if (+vn.id !== personBeziehungen.elternteilPersonId && +vn.id !== personBeziehungen.elternteilPersonId2) {
    if (!personBeziehungen.elternteilPersonId) {
      return { ...personBeziehungenUpdate, elternteilPersonId: +vn.id };
    } else {
      return { ...personBeziehungenUpdate, elternteilPersonId2: +vn.id };
    }
  }
  return undefined;
}

export function findVn(gvAngebotData: GvAngebotData): GvPerson {
  return gvAngebotData.gvPersonenData.personen.find((person) => person.personRollen.vn);
}

export function checkGvPersonBeziehung(
  person: GvPerson,
  originalGvAngebotData: GvAngebotData,
  gvAngebotData: GvAngebotData,
  personBeziehungenUpdate: GvPersonBeziehungen
): GvPersonBeziehungen {
  function checkUpdateSchwangerschaftsDaten() {
    if (personBeziehungenUpdate.schwangerschaftsInfo) {
      if (
        personBeziehungenUpdate.schwangerschaftsInfo.schwanger &&
        (!person.personBeziehungen.schwangerschaftsInfo || !person.personBeziehungen.schwangerschaftsInfo.schwanger) &&
        !person.personBeziehungen.schwangerschaftsInfo.deckung
      ) {
        personBeziehungenUpdate.schwangerschaftsInfo.deckung = true;
      } else if (
        person.personBeziehungen.schwangerschaftsInfo &&
        person.personBeziehungen.schwangerschaftsInfo.schwanger &&
        !personBeziehungenUpdate.schwangerschaftsInfo.schwanger &&
        (personBeziehungenUpdate.schwangerschaftsInfo.deckung ||
          personBeziehungenUpdate.schwangerschaftsInfo.kindsVaterPersonId)
      ) {
        personBeziehungenUpdate.schwangerschaftsInfo.kindsVaterPersonId = null;
        personBeziehungenUpdate.schwangerschaftsInfo.deckung = false;
      }
      if (
        personBeziehungenUpdate.schwangerschaftsInfo.schwanger &&
        sozVerAnstaltenDeckungsPflicht.includes(person.stammdaten.sozversanstalt) &&
        !personBeziehungenUpdate.schwangerschaftsInfo.deckung
      ) {
        personBeziehungenUpdate.schwangerschaftsInfo.deckung = true;
      }
    }
  }

  function checkUpdateElternteilBeziehungen(originalPerson: GvPerson) {
    const elternTeil1Changed =
      originalPerson.personBeziehungen.elternteilPersonId &&
      originalPerson.personBeziehungen.elternteilPersonId !== person.personBeziehungen.elternteilPersonId;
    const elternTeil2Changed =
      originalPerson.personBeziehungen.elternteilPersonId2 &&
      originalPerson.personBeziehungen.elternteilPersonId2 !== person.personBeziehungen.elternteilPersonId2;

    if (elternTeil1Changed || elternTeil2Changed) {
      // const vnElternTeil = getVnElternteil(gvAngebotData, originalPerson.personBeziehungen);
      const vnElternTeil = findVn(gvAngebotData);

      if (vnElternTeil) {
        if (
          (elternTeil1Changed && +vnElternTeil.id === originalPerson.personBeziehungen.elternteilPersonId) ||
          (elternTeil2Changed && +vnElternTeil.id === originalPerson.personBeziehungen.elternteilPersonId2)
        ) {
          personBeziehungenUpdate.vnBeziehung = undefined;
        }
      }
    }
  }

  if (person.personBeziehungen) {
    const originalPerson: GvPerson = originalGvAngebotData.gvPersonenData.personen.find(
      (oPerson) => person.id === oPerson.id
    );
    // Konstellation -> VN-Beziehung "Kind" -> Setzen des VN als Elternteil
    if (
      VN_BEZ_OPTION_VALUE_KIND === personBeziehungenUpdate.vnBeziehung &&
      (!originalPerson.personBeziehungen || VN_BEZ_OPTION_VALUE_KIND !== originalPerson.personBeziehungen.vnBeziehung)
    ) {
      const result: GvPersonBeziehungen | undefined = setzeVnAlsElternteilWennNotwendig(
        gvAngebotData,
        person.personBeziehungen,
        personBeziehungenUpdate
      );
      if (result) {
        return result;
      }
    }

    if (person.personBeziehungen.schwangerschaftsInfo) {
      checkUpdateSchwangerschaftsDaten();
    }

    // Pruefung ob sich einer der Elternteile verändert hat ->
    // wenn ja und der Elternteil ist auch VN dann wird die vnBeziehung elminiert
    if (originalPerson.personBeziehungen) {
      checkUpdateElternteilBeziehungen(originalPerson);
    }

    if (
      person.personBeziehungen.vnBeziehung !== VN_BEZ_OPTION_VALUE_KIND &&
      isElternteilVn(gvAngebotData, personBeziehungenUpdate)
    ) {
      return { ...personBeziehungenUpdate, vnBeziehung: VN_BEZ_OPTION_VALUE_KIND };
    }
  }

  return { ...personBeziehungenUpdate };
}

/**
 * Prueft ob Person als Elternteil vorkommt und entfernt diese Beziehung wenn notwendig
 *
 */
export function entferneElternteilBeziehungWennNotwendig(
  gvPerson: GvPerson,
  gvAngebotData: GvAngebotData
): GvAngebotData {
  return produce(gvAngebotData, (draft: GvAngebotData) => {
    for (const currentPerson of draft.gvPersonenData.personen) {
      if (currentPerson.id !== gvPerson.id && currentPerson.personBeziehungen) {
        entferneElternteilBeziehung(gvPerson, currentPerson);
      }
    }
  });
}

/**
 * Prueft ob Person als Kindsvater vorkommt und entfernt diese Beziehung wenn notwendig
 *
 */
export function entferneKindsVaterBeziehungWennNotwendig(
  gvPerson: GvPerson,
  gvAngebotData: GvAngebotData
): GvAngebotData {
  return produce(gvAngebotData, (draft: GvAngebotData) => {
    for (const currentPerson of draft.gvPersonenData.personen) {
      if (
        currentPerson.id !== gvPerson.id &&
        currentPerson.personBeziehungen &&
        currentPerson.personBeziehungen.schwangerschaftsInfo &&
        +gvPerson.id === currentPerson.personBeziehungen.schwangerschaftsInfo.kindsVaterPersonId
      ) {
        currentPerson.personBeziehungen.schwangerschaftsInfo.kindsVaterPersonId = undefined;
      }
    }
  });
}

export function entferneElternteilBeziehung(person: GvPerson, potentiellesKind1: GvPerson): void {
  if (potentiellesKind1.personBeziehungen.elternteilPersonId === +person.id) {
    if (potentiellesKind1.personBeziehungen.elternteilPersonId2) {
      potentiellesKind1.personBeziehungen.elternteilPersonId = potentiellesKind1.personBeziehungen.elternteilPersonId2;
      potentiellesKind1.personBeziehungen.elternteilPersonId2 = undefined;
    } else {
      potentiellesKind1.personBeziehungen.elternteilPersonId = undefined;
    }
  } else if (potentiellesKind1.personBeziehungen.elternteilPersonId2 === +person.id) {
    potentiellesKind1.personBeziehungen.elternteilPersonId2 = undefined;
  }
}

export function getGvPersonenInfo(person: GvPerson | Person): string {
  return `${person.stammdaten.vorname ? person.stammdaten.vorname : ''} ${person.stammdaten.name}${
    person.stammdaten.gebdatum ? ', ' + getFormattedDateString(person.stammdaten.gebdatum) : ''
  }`;
}

export function updateVnBeziehungenWennNotwendig(...personen: GvPerson[]): GvPerson[] {
  return produce(personen, (draftPersonen: GvPerson[]) => {
    draftPersonen.forEach((person: GvPerson) => {
      if (person.personBeziehungen) {
        if (person.personBeziehungen.vnBeziehung) {
          // VN Beziehung aufrechtlassen wenn
          if (!isElternteilVnForPersonen(personen, person.personBeziehungen)) {
            person.personBeziehungen.vnBeziehung = undefined;
          } else {
            if (person.personBeziehungen.vnBeziehung !== VN_BEZ_OPTION_VALUE_KIND) {
              person.personBeziehungen.vnBeziehung = VN_BEZ_OPTION_VALUE_KIND;
            }
          }
        } else {
          if (isElternteilVnForPersonen(personen, person.personBeziehungen)) {
            person.personBeziehungen.vnBeziehung = VN_BEZ_OPTION_VALUE_KIND;
          }
        }
      }
    });
  });
}
